import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import api from "../api/api.js";

import {
  ArrowLeft,
  ClipboardList,
  BarChart3,
  FileText,
  CheckSquare,
  Briefcase,
  Users,
  Package,
  ImageIcon,
} from "lucide-react";

export default function ProjectDetailMobile() {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const [todoFilter, setTodoFilter] = useState("all");
  const [notes, setNotes] = useState("");
  const [activeSection, setActiveSection] = useState("notes");

  const {
    data: lineItems = [],
    isLoading: loading,
    error: lineItemsError,
  } = useQuery({
    queryKey: ["lineItems", projectId],
    queryFn: async () => {
      const response = await api.get(`/projects/${projectId}/line-items/`);
      return response.data;
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  const {
    data: todos = [],
    isLoading: todosLoading,
    error: todosErrorObj,
  } = useQuery({
    queryKey: ["projectTodos", projectId],
    queryFn: async () => {
      const response = await api.get(`/todos/?project_id=${projectId}`);
      return response.data;
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 150;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
      setActiveSection(sectionId);
    }
  };

  const error = lineItemsError?.message || "";
  const todosError = todosErrorObj?.message || "";

  const metrics = {
    totalLineItems: lineItems.length,
    totalTodos: todos.length,
    completedTodos: todos.filter((t) => t.status === "Completed").length,
    overdueTodos: todos.filter((t) => t.status === "Overdue").length,
  };

  const getFilteredTodos = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch (todoFilter) {
      case "today":
        return todos.filter((todo) => {
          const deadline = new Date(todo.deadline);
          deadline.setHours(0, 0, 0, 0);
          return deadline.getTime() === today.getTime();
        });
      case "overdue":
        return todos.filter((todo) => todo.status === "Overdue");
      case "completed":
        return todos.filter((todo) => todo.status === "Completed");
      case "all":
      default:
        return todos;
    }
  };

  const filteredTodos = getFilteredTodos();

  const LoadingSpinner = () => (
    <div className="flex items-center justify-center py-8">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600"></div>
      <span className="ml-3 text-gray-600">Loading...</span>
    </div>
  );

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "bg-green-100 text-green-800 border border-green-200";
      case "In Progress":
        return "bg-yellow-100 text-yellow-800 border border-yellow-200";
      case "Overdue":
        return "bg-red-100 text-red-800 border border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border border-gray-200";
    }
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case "High":
        return "bg-red-100 text-red-800 border border-red-200";
      case "Medium":
        return "bg-yellow-100 text-yellow-800 border border-yellow-200";
      case "Low":
        return "bg-green-100 text-green-800 border border-green-200";
      default:
        return "bg-gray-100 text-gray-800 border border-gray-200";
    }
  };

  const navigationItems = [
    { id: "notes", label: "Notes", icon: FileText },
    { id: "todos", label: "Todos", icon: CheckSquare },
    { id: "contacts", label: "Contacts", icon: Users },
    { id: "materials", label: "Materials", icon: Package },
    { id: "documents", label: "Documents", icon: ImageIcon },
  ];

  return (
    <div className="bg-gray-50">
      {/* Mobile header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="px-4 py-3">
          <button
            className="text-gray-600 hover:text-gray-900 mb-2 flex items-center gap-2 transition-colors"
            onClick={() => navigate("/ProjectList")}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Projects
          </button>
          <h1 className="text-xl font-light text-gray-900">Project Details</h1>
        </div>
      </div>

      {/* Mobile horizontal navigation */}
      <div className="fixed top-[72px] left-0 right-0 z-20 bg-white border-b border-gray-200 shadow-sm">
        <div className="overflow-x-auto">
          <div className="flex gap-2 px-4 py-3 min-w-max">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeSection === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md transition-all whitespace-nowrap ${
                    isActive
                      ? "bg-blue-500 text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="px-4 py-6 pt-16 pb-32">
        {/* Metrics cards - stacked on mobile */}
        <div className="space-y-4 mb-6">
          <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <ClipboardList className="w-5 h-5 text-gray-600" />
              </div>
              <span className="text-sm">Total Line Items</span>
            </div>
            <div className="text-3xl font-light text-gray-900">
              {metrics.totalLineItems}
            </div>
            <div className="text-sm text-gray-500">
              Materials & resources tracked
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <CheckSquare className="w-5 h-5 text-gray-600" />
              </div>
              <span className="text-sm">Active Todos</span>
            </div>
            <div className="text-3xl font-light text-gray-900">
              {metrics.totalTodos}
            </div>
            <div className="text-sm text-gray-500">
              {metrics.completedTodos} completed, {metrics.overdueTodos} overdue
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <BarChart3 className="w-5 h-5 text-gray-600" />
              </div>
              <span className="text-sm">Completion Rate</span>
            </div>
            <div className="text-3xl font-light text-gray-900">
              {metrics.totalTodos > 0
                ? Math.round(
                    (metrics.completedTodos / metrics.totalTodos) * 100
                  )
                : 0}
              %
            </div>
            <div className="text-sm text-gray-500">Tasks completed on time</div>
          </div>
        </div>

        {/* Notes section */}
        <div
          id="notes"
          className="bg-white rounded-lg border border-gray-200 p-4 mb-6 shadow-sm scroll-mt-32"
        >
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-4 h-4 text-gray-600" />
            <h2 className="text-lg font-light text-gray-900">Project Notes</h2>
          </div>
          <textarea
            className="w-full h-32 p-3 border border-gray-300 rounded-md bg-white text-gray-900 shadow-sm text-sm"
            placeholder="Add notes about this project..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <div className="flex justify-end mt-3">
            <button className="px-4 py-2 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 rounded-md transition-colors shadow-sm text-sm">
              Save Notes
            </button>
          </div>
        </div>

        {/* Todos section */}
        <div
          id="todos"
          className="bg-white rounded-lg border border-gray-200 p-4 mb-6 shadow-sm scroll-mt-32"
        >
          <div className="flex items-center gap-2 mb-4">
            <CheckSquare className="w-4 h-4 text-gray-600" />
            <h2 className="text-lg font-light text-gray-900">Todos</h2>
          </div>

          {/* Filter buttons - wrapped on mobile */}
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={() => setTodoFilter("today")}
              className={`px-3 py-2 rounded-md text-sm transition-colors shadow-sm ${
                todoFilter === "today"
                  ? "bg-blue-500 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
              }`}
            >
              Today
            </button>
            <button
              onClick={() => setTodoFilter("overdue")}
              className={`px-3 py-2 rounded-md text-sm transition-colors shadow-sm ${
                todoFilter === "overdue"
                  ? "bg-blue-500 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
              }`}
            >
              Overdue
            </button>
            <button
              onClick={() => setTodoFilter("completed")}
              className={`px-3 py-2 rounded-md text-sm transition-colors shadow-sm ${
                todoFilter === "completed"
                  ? "bg-blue-500 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
              }`}
            >
              Completed
            </button>
            <button
              onClick={() => setTodoFilter("all")}
              className={`px-3 py-2 rounded-md text-sm transition-colors shadow-sm ${
                todoFilter === "all"
                  ? "bg-blue-500 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
              }`}
            >
              All
            </button>
          </div>

          {todosLoading && <LoadingSpinner />}
          {todosError && (
            <div className="text-red-600 p-3 bg-red-50 rounded-lg border border-red-200 text-sm">
              {todosError}
            </div>
          )}
          {!todosLoading && !todosError && filteredTodos.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">No todos found for this filter.</p>
            </div>
          )}

          <div className="space-y-3">
            {filteredTodos.map((todo) => (
              <div
                key={todo.id}
                className="border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow bg-white"
              >
                <h3 className="text-gray-900 mb-1 text-sm font-medium">
                  {todo.task_name}
                </h3>
                <p className="text-xs text-gray-600 mb-3">{todo.description}</p>
                <div className="flex flex-wrap gap-2 mb-2">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                      todo.status
                    )}`}
                  >
                    {todo.status}
                  </span>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${getUrgencyColor(
                      todo.urgency
                    )}`}
                  >
                    {todo.urgency}
                  </span>
                </div>
                <div className="space-y-1 text-xs text-gray-500">
                  <div>👤 {todo.assign_to}</div>
                  <div>📅 {new Date(todo.deadline).toLocaleDateString()}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Contacts section - stacked on mobile */}
        <div id="contacts" className="space-y-6 mb-6 scroll-mt-32">
          <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <Briefcase className="w-4 h-4 text-gray-600" />
              <h2 className="text-lg font-light text-gray-900">
                Project Contacts
              </h2>
            </div>
            <p className="text-xs text-gray-600 mb-4">
              Subcontractors, vendors, and partners
            </p>
            <div className="space-y-3">
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-xs text-gray-500 text-center">
                  No project contacts added yet
                </p>
              </div>
              <button className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-700 transition-colors text-sm">
                + Add Project Contact
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-4 h-4 text-gray-600" />
              <h2 className="text-lg font-light text-gray-900">
                Customer Contacts
              </h2>
            </div>
            <p className="text-xs text-gray-600 mb-4">
              Client contacts and stakeholders
            </p>
            <div className="space-y-3">
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-xs text-gray-500 text-center">
                  No customer contacts added yet
                </p>
              </div>
              <button className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-700 transition-colors text-sm">
                + Add Customer Contact
              </button>
            </div>
          </div>
        </div>

        {/* Materials section */}
        <div
          id="materials"
          className="bg-white rounded-lg border border-gray-200 p-4 mb-6 shadow-sm scroll-mt-32"
        >
          <div className="flex items-center gap-2 mb-4">
            <Package className="w-4 h-4 text-gray-600" />
            <h2 className="text-lg font-light text-gray-900">
              Materials & Line Items
            </h2>
          </div>

          {loading && <LoadingSpinner />}
          {error && (
            <div className="text-red-600 p-3 bg-red-50 rounded-lg border border-red-200 text-sm">
              {error}
            </div>
          )}
          {!loading && !error && lineItems.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">No materials found for this project.</p>
            </div>
          )}

          <div className="space-y-3">
            {lineItems.map((item) => (
              <div
                key={item.id}
                className="border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow bg-white"
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-gray-900 text-sm font-medium">
                    {item.name}
                  </h3>
                  <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-md font-medium">
                    {item.category}
                  </span>
                </div>
                <p className="text-xs text-gray-600 mb-2">{item.description}</p>
                <div className="text-xs text-gray-900">
                  {item.quantity !== null && item.quantity !== undefined
                    ? `${item.quantity} ${item.unit ?? ""}`.trim()
                    : "Quantity not specified"}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Documents section - stacked on mobile */}
        <div id="documents" className="space-y-6 scroll-mt-32">
          <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-4 h-4 text-gray-600" />
              <h2 className="text-lg font-light text-gray-900">Documents</h2>
            </div>
            <p className="text-xs text-gray-600 mb-4">
              Contracts, signed documents, and files
            </p>
            <div className="space-y-3">
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-xs text-gray-500 text-center">
                  No documents uploaded yet
                </p>
              </div>
              <button className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-700 transition-colors text-sm">
                + Upload Document
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <ImageIcon className="w-4 h-4 text-gray-600" />
              <h2 className="text-lg font-light text-gray-900">
                Project Images
              </h2>
            </div>
            <p className="text-xs text-gray-600 mb-4">
              Photos and images related to this project
            </p>
            <div className="space-y-3">
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-xs text-gray-500 text-center">
                  No images uploaded yet
                </p>
              </div>
              <button className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-700 transition-colors text-sm">
                + Upload Image
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
